import {
    BankAgency,
    ContactType,
    FilterParty,
    Organization,
    PartyContactMechanism,
    Person,
    Relation,
    SearchBankAgency,
    SearchOrganization,
    SearchPerson,
    SearchSupplier,
    Third,
    ThirdType
} from "@/types";
import {settings} from "@/settings";
import {deepClone, enrichClassifications, getEntity, globalConfig, headers, mountSelectFields, Paths, sanitizeKey, UON} from "@/utils";
import axios from "axios";
import {formatDateLocale, getYearsOfDate, isValidDate} from "@/utils/configuration/formatters-config";
import store from "@/store";
import {useApi} from "@/requests/useApi";
import {buildManagerRelation, buildPartyTypePreference} from "@/commons/party-middleware";
import i18n from "@/i18n";
import {buildJobs} from "./thirdService";
import { getQcParty } from "./filter/filterService";
import {getNature} from "@/store/services/picklist";

const odm_party = 'odm_party'

/** One GET per `resourceUid`; parallel callers share the same request; each consumer gets a clone (buildAddress mutates). */
const addressInflight = new Map<string, Promise<any>>();
const addressResult = new Map<string, any>();
const frenchPostalCodeInflight = new Map<string, Promise<any>>();
const frenchPostalCodeResult = new Map<string, any>();

async function getAddressEntityCached(resourceUid: string) {
    if (!resourceUid) {
        return undefined;
    }
    if (addressResult.has(resourceUid)) {
        return deepClone(addressResult.get(resourceUid));
    }
    let inflight = addressInflight.get(resourceUid);
    if (!inflight) {
        inflight = getEntity('odm-party', 'address', resourceUid)
            .then((data: any) => {
                addressResult.set(resourceUid, data);
                return data;
            })
            .finally(() => {
                addressInflight.delete(resourceUid);
            });
        addressInflight.set(resourceUid, inflight);
    }
    const data = await inflight;
    return deepClone(data);
}

async function getFrenchPostalCodeEntityCached(resourceUid: string) {
    if (!resourceUid) {
        return undefined;
    }
    if (frenchPostalCodeResult.has(resourceUid)) {
        return deepClone(frenchPostalCodeResult.get(resourceUid));
    }
    let inflight = frenchPostalCodeInflight.get(resourceUid);
    if (!inflight) {
        inflight = getEntity('odm-party', 'frenchpostalcode', resourceUid)
            .then((data: any) => {
                frenchPostalCodeResult.set(resourceUid, data);
                return data;
            })
            .finally(() => {
                frenchPostalCodeInflight.delete(resourceUid);
            });
        frenchPostalCodeInflight.set(resourceUid, inflight);
    }
    const data = await inflight;
    return deepClone(data);
}

function getOperator(table: string, field: string, operator: string, value: any, parameter: string) {
    return {
        "expression": {
            "table": table,
            "field": field,
            "operator": operator,
            "value": value,
            "parameter": parameter
        }
    }
}

function getValidUniformOdmName(objectType: string, resourceUid?: string | '', systemUid?: string | '') {
    if (resourceUid && resourceUid !== '') {
        return {
            objectType: objectType,
            systemUid: systemUid,
            resourceUid: resourceUid,
        }
    } else {
        return undefined
    }
}

export function getContactType(id: string, label: string, objectType: string, fields: string[]) {
    return {
        id: id,
        label: i18n.global.t(`party.typeComunication.${label}`),
        config: {
            objectType: objectType,
            fields: fields
        }
    }
}

export async function getNatureIncome(code: string) {
    const natures : any = await getNature();
    return natures.items.filter((item: any) => item.config.internalCode === code)
}

function blockContactMechanismType(party: Organization | Person) {
    party.contacts?.forEach((contact: PartyContactMechanism) => {
        if (contact.contactMechanism.objectType !== '') {
            contact.contactMechanism.isDisabled = true
            contact.contactMechanism.blocked = true
        }
    })
}

export function buildExistingRevenues(person: Person, natureIncome: any) {
    if (person?.revenues && person.revenues?.length > 0) {
        person.revenues.forEach((item: any) => {
            item.detail.forEach((el: any) => {
                const isNatureIncome = natureIncome.some((na: any) => na.value === el.nature.resourceUid)
                const type = isNatureIncome ? 'INCOME' : 'EXPENSE'
                el.type = type
                el.value = el.value.amount
                el.periodicity = {
                    type: {
                        label: i18n.global.t(`party.periodicity.${el.periodicity.resourceUid}`),
                        value: el.periodicity.resourceUid
                    }
                }
                el.nature = {
                    type: {
                        label: el.nature.resourceUid,
                        value: el.nature.resourceUid
                    }
                }
                el.validity = {
                    from: el.validity?.from,
                    until: el.validity?.until,
                }
            })
        })
    }
    else {
        person.revenues = [{ ...store.getters["demandModule/getThirdAnalysisInit"] }]
    }
}

export const getParty = async (type: string, resourceUid: string) => {
    return await useApi(
        UON.SystemUid.odmParty,
        type.split("party-")[1]
            .replace("F", "f")
            .toLowerCase()
    ).get(resourceUid);
}

export async function getThird(resourceUid: string, type: string, whenCreated: string): Promise<Third> {
    
    const isPerson = type.toUpperCase().includes('PERSON')    
    const { response } = await getParty(type, resourceUid);
    const promisesContacts = response.contacts?.map((item: any) => buildContacts(item))
    await Promise.all(promisesContacts)

    const natureIncome = await getNatureIncome('INCOME')
    for (const item of response.roles) {
        if (item.role && item.role.resourceUid) {
            item.role = {
                type: {
                    value: item.role.resourceUid,
                    label: item.role.resourceUid[0] + item.role.resourceUid.substring(1).toLowerCase()
                }
            }
        } else {
            item.role = {
                type: {
                    value: "",
                },
            };
        }
        if (item.status) {
            item.status = {
                type: {
                    value: item.status.resourceUid,
                    label: item.status.resourceUid[0] + item.status.resourceUid.substring(1).toLowerCase()
                }
            }
        } else {
            item.status = {
                type: {
                    value: "",
                },
            };
        }
        item.isDisabled = true
        if (item.validity) {
            item.validity.from = item.validity?.from ? item.validity.from : ''
            item.validity.until = item.validity?.until ? item.validity.until : ''
        } else {
            item.validity = {
                from: '',
                until: ''
            }
        }

        await buildPartyTypePreference(item.preferences, response.addresses);


    }

    if (response.classifications?.length) {
        await enrichClassifications(response.classifications)
        response.classifications.forEach((node: any) => {
            node.isDisabled = true
        })
    }
    response.correspondents?.forEach(async (cor: any) => {
        if(!cor.qualificationType){
            cor.qualificationType = {
                resourceUid:null
            }
        }

    })
    if (response.taxGeographicZone && response.taxGeographicZone.resourceUid) {
        response.taxGeographicZone = response.taxGeographicZone.resourceUid
    }

    response.financialExposures = response.financialExposures.map((item: any) => buildFinancialExposure(item))

    const promisesAccounts = response.accounts.map(async (item: any) => await buildBankAccount(item))
    await Promise.all(promisesAccounts)

    for (const correspondents of response.correspondents) {
        for (const contactCorrespondent of correspondents.contactCorrespondent) {
            contactCorrespondent.contactMechanism = await getEntity('odm-party', 'contactmechanism', contactCorrespondent.contactMechanism.resourceUid)
            await buildContacts(contactCorrespondent)
        }
        if(correspondents && correspondents.qualificationType) {
            correspondents.qualificationType.type = {
                value: correspondents.qualificationType?.resourceUid
            }
        }
    }

    let person = isPerson ? response : undefined
    const organization = !isPerson ? response : undefined

    if (response.objectType.includes('person')) {
        if (person && person.revenues && person.revenues.length == 0) {
             person.revenues = []
        } else {
            buildExistingRevenues(person, natureIncome);
        }
        if (person.businessData) {
            person.homemakers = person.businessData.homemakers
            person.retirementPassage = person.businessData.retirementPassage
            person.birthChildren = person.businessData.birthChildren
        }
        person.nationality = {
            type: {
                label: (person.citizenships && person.citizenships.length > 0) ? person.citizenships[0].country.resourceUid : '',
                value: (person.citizenships && person.citizenships.length > 0) ? person.citizenships[0].country.resourceUid : '',
            }
        }
        if (person.birthDate && isValidDate(person.birthDate, 'DD/MM/YYYY', 'fr')) {
            person.age = getYearsOfDate(formatDateLocale(person.birthDate, 'DD/MM/YYYY', 'us', 'YYYY-MM-DD'))
        }
        if (person.birthplace) {
            const { resourceUid, postalCode, city, department, country } = await getFrenchPostalCodeEntityCached(person.birthplace.resourceUid)
            person.birthplace = {
                type : {
                    id: resourceUid ?? '',
                    label: `${postalCode} - ${city}`,
                    config: {
                        city: city ?? '',
                        department: department ?? '',
                        country: {
                            id: country?.resourceUid ?? '',
                            label: country?.resourceUid ?? ''
                        },
                    }
                },
                postalCode: {
                    id: resourceUid ?? '',
                    label: `${postalCode} - ${city}`,
                    config: {
                        city: city ?? '',
                        department: department ?? '',
                        country: {
                            id: country?.resourceUid ?? '',
                            label: country?.resourceUid ?? ''
                        },
                    }, 
                    postal_code: person.birthplace ? person.birthplace.resourceUid : ''
                }
            }
        } else {
            person.birthplace = {
                type: {
                    id: person.birthplace ? person.birthplace.resourceUid : '',
                    label: person.birthplace ? person.birthplace.resourceUid : '',
                    config: {
                        city: '',
                        department : '',
                        country: {
                            id: '',
                            label: ''
                        },
                    }
                },
                postalCode: person.birthplace ? person.birthplace.resourceUid : ''
            }

        }

        if (
            person.businessData &&
            person.businessData.birthplaceCountry &&
            person.businessData.birthplaceCountry !== "FR"
        ) {
            if (!person.birthplace) {
                person.birthplace = { type: {} }
            }
            if (!person.birthplace.type) {
                person.birthplace.type = {}
            }
            if (!person.birthplace.type.config) {
                person.birthplace.type.config = {
                    city: "",
                    department: "",
                    country: { id: "", label: "" },
                }
            }
            if (!person.birthplace.type.config.country) {
                person.birthplace.type.config.country = { id: "", label: "" }
            }
            person.birthplace.type.config.country.id = person.businessData.birthplaceCountry
            person.birthplace.type.config.country.label = person.businessData.birthplaceCountry
            person.birthplace.type.config.city = person.businessData.birthplaceCity || ""
            person.birthplace.type.label = person.businessData.birthplaceCity || ""
        }

        person.title = {
            type: {
                label: person.title ? person.title.resourceUid : '',
                value: person.title ? person.title.resourceUid : ''
            }
        }
        let maritalStatus = ''
        let matrimonialRegime = ''
        if (person.maritalStatuses && person.maritalStatuses.length > 0) {
            maritalStatus = person.maritalStatuses[0].maritalStatus.resourceUid
            matrimonialRegime = person.maritalStatuses[0].matrimonialRegime.resourceUid
        }
        person.currentMatrimonialRegime = {
            type: {
                label: matrimonialRegime,
                value: matrimonialRegime
            }
        }
        person.currentMaritalStatus = {
            type: {
                label: maritalStatus,
                value: maritalStatus
            }
        }
        if (person.gender) {
            person.gender = {
                type: {
                    label: person.gender.resourceUid || '',
                    value: person.gender.resourceUid || ''
                }
            }
        }
        
        if (
            !person.gender && person.title.type.value
        ) {
            person.gender = {
                type: {
                    value: person?.title?.type.value === "M" ? "MALE" : "FEMALE",
                },
            };
        }

       
        
        person.ids.forEach((node: any) => {

            if (node.documentType) {
                node.documentType.type = {
                    value: node.documentType.resourceUid,
                    label: node.documentType.resourceUid?.toString() !== "" ? i18n.global.t("middleOffice.party.person.identityDoc.docTypes." + node.documentType.resourceUid) : ''
                }
            }
            else {
                node.documentType = {
                    type: {
                        value: '',
                        label: ''
                    }
                }
            }
            node.validity = node.validity || { from: '', until: '' }
        })

        if (person.jobs.length) {
            person = await buildJobs(person, false)
            person.jobs.forEach((element: any) => {
                if (!element.occupationType) {
                    element.occupationType = {
                        type: {
                            id: undefined,
                            label: undefined
                        },
                    }
                }
                if (!element.contractType) {
                    element.contractType = {
                        type: {
                            id: undefined,
                            label: undefined
                        },
                    }
                }

            });
        }
        const partyRelations = (await buildManagerRelation(person))?.load()
        if (partyRelations) {
            person.relations = buildRelations(partyRelations, person);
        }
    } else {
        organization.facilities.map(async (item: any) => await buildFacility(item))
        for (const item of response.roles) {
            await buildPartyTypePreference(item.preferences, response.facilities);
        }
        if (organization.legalCategory) {
            organization.legalCategory.type = {
                value: organization.legalCategory.resourceUid,
            }
        } else {
            organization.legalCategory = {
                type: {
                    value: ''
                }
            }
        }
        if (organization.codeNACE && organization.codeNACE.resourceUid) {
            organization.codeNACE = {
                type: {
                    label: organization.codeNACE.resourceUid?.toString() !== "" ? `${organization.codeNACE.resourceUid} - ${i18n.global.t('picklists.codeNace.'.concat(organization.codeNACE.resourceUid))}` : '',
                    value: organization.codeNACE.resourceUid,
                }
            }
        } else {
            organization.codeNACE = {
                type: {
                    value: '',
                    label: ''
                }
            }
        }
        if (organization.codeQualificationType && organization.codeQualificationType.resourceUid) {
            organization.codeQualificationType = {
                type: {
                    label: organization.codeQualificationType.resourceUid?.toString() !== "" ? `${i18n.global.t('picklists.qualificationTypeGroupe.'.concat(organization.codeQualificationType.resourceUid))}` : '',
                    value: organization.codeQualificationType.resourceUid,
                }
            }
        } else {
            organization.codeQualificationType = {
                type: {
                    value: '',
                    label: ''
                }
            }
        }

        if (organization.activitySector) {
            organization.activitySector.type = {
                value: organization.activitySector.resourceUid,
            }
        } else {
            organization.activitySector = {
                type: {
                    value: ''
                }
            }
        }
        if (organization.balanceSheets && organization.balanceSheets.length !== 0) {
            const ListBalanceSheets: any[] = []
            organization.balanceSheets.forEach(async (item: any) => {
                const balanceSheet = await buildBalanceSheets(item)
                ListBalanceSheets.push(balanceSheet)
            });
            organization.balanceSheets = ListBalanceSheets
        }

        ///organization.balanceSheets = organization.balanceSheets.map(async (item: any) => await buildBalanceSheets(item))

        const partyRelations = (await buildManagerRelation(organization))?.load()
        if (partyRelations) {
            organization.relations = buildRelations(partyRelations, organization);
        }else {
            organization.manager = store.getters['demandModule/getManagerInit']
        }


/*        const partyRelations = (await buildManagerRelation(organization))?.loadAscRelations();
        if (partyRelations) {
            const relations = [];
            for (const rel of partyRelations) {
                const path = rel.partyAsc.objectType.toUpperCase().includes('PERSON') ? Paths.frenchPerson : Paths.frenchOrganization
                const { response } = await useApi(UON.SystemUid.odmParty, path).get(rel.partyAsc.resourceUid);
                relations.push({
                    ...rel,
                    relation: {
                        type: {
                            value: 'ascending',
                        }
                    },
                    name: (response && response.firstName) || '',
                });
            }
            organization.relations = relations;
            mountSelectFields(organization.relations, ['relationType'])
        }
        else {
            organization.manager = store.getters['demandModule/getManagerInit']
        }*/

        response.addresses.forEach((adresse: any) => {
            delete adresse.livingSituation
        });

        organization.facilities.forEach((adresse: any) => {
            delete adresse.livingSituation
        });
    }

    return {
        id: getValidUniformOdmName(response.objectType, response.resourceUid, response.systemUid),
        type: { id: type, label: isPerson ? "Personne physique" : "Personne morale" },
        isDisabled: true,
        whenCreated: whenCreated,
        organization: organization,
        person: person,
        addresses: response.addresses,
        accounts: response.accounts,
        financialExposures: response.financialExposures,
        relations: response.relations,
        roles: response.roles,
        correspondents: response.correspondents,
        classifications: response.classifications,
    }
}


export function buildFinancialExposure(node: any) {

    return {
        resourceUid: node.resourceUid,
        objectType: node.objectType,
        systemUid: node.systemUid,
        outstandingType: {
            value: node.outstandingType?.resourceUid ? node.outstandingType?.resourceUid : "",
            label: node.outstandingType?.resourceUid ? node.outstandingType?.resourceUid : ""
        },
        financialInstitution: {
            value: node.financialInstitution?.resourceUid ? node.financialInstitution?.resourceUid : "",
            label: node.financialInstitution?.resourceUid ? node.financialInstitution?.resourceUid : ""
        },
        grossOutstanding: node.grossOutstanding?.amount,
        netOutstanding: node.netOutstanding?.amount,
        residualValue: node.residualValue?.amount,
        flagFinancialPool: node.flagFinancialPool,
        flagRiskPool: node.flagRiskPool,
        shareFinancialPool: node.shareFinancialPool,
        shareRiskPool: node.shareRiskPool,
        effectiveDate: node.effectiveDate,
        contractReference: node.contractReference,
        contractValidity: {
            from: node.contractValidity?.from,
            until: node.contractValidity?.until
        },
        businessData: node.businessData ? node.businessData : undefined

    }
}


export async function buildBankAccount(node: any) {
    node.bankAccount = await getEntity('odm-party', 'frenchbankaccount', node.bankAccount.resourceUid)

    node.bankAccount.validity = {
        from: node.bankAccount.validity?.from ? node.bankAccount.validity.from : '',
        until: node.bankAccount.validity?.until ? node.bankAccount.validity.until : ''
    }
    // node.bankAccount.validity.from = node.bankAccount.validity?.from ? node.bankAccount.validity.from : ''
    // node.bankAccount.validity.until = node.bankAccount.validity?.until ? node.bankAccount.validity.until : ''

    node.bankAccount.type = {
        id: node.bankAccount.type.resourceUid,
        label: node.bankAccount.type.resourceUid[0] + node.bankAccount.type.resourceUid.substring(1).toLowerCase()
    }

    node.bankAccount.status = {
        id: node.bankAccount.status.resourceUid,
        label: i18n.global.t("party.account.statut."+ node.bankAccount.status.resourceUid.toLowerCase())
    }
}

function samePartyId(left: unknown, right: unknown): boolean {
    if (left === undefined || left === null || right === undefined || right === null || left === '' || right === '') {
        return false;
    }
    return String(left) === String(right);
}

function areSwappedPartyRows(left: any, right: any): boolean {
    if (!left || !right || left === right || samePartyId(left.id, right.id)) {
        return false;
    }
    return samePartyId(left.party_asc_id, right.party_desc_id) &&
        samePartyId(left.party_desc_id, right.party_asc_id) &&
        !samePartyId(left.party_asc_id, left.party_desc_id);
}

function getRelationDisplayName(item: any): string {
    if (item._discriminator === ThirdType.ORGANIZATION ||
        (typeof item._discriminator === 'string' && item._discriminator.toUpperCase().includes('ORGANIZATION'))) {
        return !item.name ? item.commercial_name : item.name;
    }
    return item.family_name || item.familyname || '';
}

function areMirroredRelationRows(left: any, right: any): boolean {
    if (!areSwappedPartyRows(left, right)) {
        return false;
    }
    const leftType = String(left.relation_type_code ?? '');
    const rightType = String(right.relation_type_code ?? '');
    if (leftType === rightType) {
        return true;
    }
    const relationTypes: any[] = store.getters["picklistModule/relationTypeItems"] || [];
    return relationTypes.some((type: any) => {
        const code = String(type.value ?? '');
        const inverse = String(type.inverse_label ?? '');
        return (code === leftType && inverse === rightType) || (code === rightType && inverse === leftType);
    });
}

/** Older saves stored the same link twice with the parties swapped. Both parties must read the
 *  same record, otherwise each one sees itself as the descendant of its own copy. */
function dropSwappedDuplicates(items: any[]): any[] {
    return items.filter((item) => {
        const mirror = items.find((other) => areMirroredRelationRows(item, other));
        return !mirror || String(item.id) < String(mirror.id);
    });
}

export function buildRelations(partyRelations: any, organization: any) {
    const items = dropSwappedDuplicates(Array.isArray(partyRelations) ? partyRelations : []);
    const currentUid = organization?.resourceUid;
    const listRelation = [];

    for (const item of items) {
        const name: string = getRelationDisplayName(item);
        // The party stored as descendant reads the link as ascending; the other side reads it
        // reversed, which is what shows the inverse relation type label.
        const direction = samePartyId(currentUid, item.party_desc_id) ? 'ascending' : 'descending';
        listRelation.push(buildRelation(item, direction, name));
    }

    return listRelation;
}


function buildRelation(item: any, relation: string, name: string): Relation {
    return {
        objectType: 'odm.party.partyrelation',
        systemUid: 'odm-party',
        resourceUid: item.id,
        name: name,
        partyAsc: {
            resourceUid: item.party_asc_id
        },
        partyDesc: {
            resourceUid: item.party_desc_id
        },
        relation: {
            type: {
                value: relation,
            }
        },
        relationType: {
            objectType: 'odm.party.relationtype',
            resourceUid: item.relation_type_code,
            type: {
                value: item.relation_type_code
            }
        },
        validity: {
            from: item.validity_from,
            until: item.validity_until
        },
        sharingRate: item.sharing_rate

    };
}

async function buildContacts(node: any) {

    let type
    if(node.contactMechanism ){
        if (node.contactMechanism.objectType.includes('emailcontact')) {
            node.contactMechanism = await getEntity('odm-party', 'emailcontact', node.contactMechanism.resourceUid)
            type = getContactType(ContactType.EMAIL_CONTACT, "E-mail", "odm.party.contactmechanism.emailcontact", ["emailAddress", "objectType", "systemUid", "resourceUid", "validity", "nonSolicitation", "daaq", "consent", "facility"])
        } else if (node.contactMechanism.objectType.includes('phonecontact')) {
            node.contactMechanism = await getEntity('odm-party', 'phonecontact', node.contactMechanism.resourceUid)
            type = getContactType(ContactType.PHONE_CONTACT, "Phone", "odm.party.contactmechanism.phonecontact", ["phoneNumber", /*"country",*/ "acceptSMS", "objectType", "systemUid", "resourceUid", "validity", "nonSolicitation", "daaq"])
        } else if (node.contactMechanism.objectType.indexOf('messengercontact')) {
            node.contactMechanism = await getEntity('odm-party', 'messengercontact', node.contactMechanism.resourceUid)
            type = getContactType(ContactType.MESSENGER_CONTACT, "Messenger", "odm.party.contactmechanism.messengercontact", ["identifier", "objectType", "systemUid", "resourceUid", "validity", "nonSolicitation", "daaq"])
        } else if (node.contactMechanism.objectType.indexOf('postalmailcontact')) {
            node.contactMechanism = await getEntity('odm-party', 'postalmailcontact', node.contactMechanism.resourceUid)
            type = getContactType(ContactType.POSTAL_MAIL_CONTACT, "Postal Mail", "odm.party.contactmechanism.postalmailcontact", ["address", "objectType", "systemUid", "resourceUid", "validity", "nonSolicitation", "daaq"])
        }
        node.contactMechanism.type = type
        node.contactMechanism.type.config.disable = true
        node.contactMechanism.isDisabled = true
        node.contactMechanism.blocked = true
    }
}

export async function buildFacility(node: any) {
    if (node.facilityType) {
        node.facilityType.type = {
            value: node.facilityType.resourceUid,
        }
    } else {
        node.facilityType = {
            type: {
                value: ''
            }
        }
    }
    const facility = await buildAddress(node)
    if (!facility.address) {
        facility.address = { ...store.getters["thirdModule/getAddressInit"] }
    }

}

export async function buildAddress(node: any) {
    if (node.address && node.address.resourceUid) {
        node.address = await getAddressEntityCached(node.address.resourceUid)
        const postalCodeUid = node.address?.postalCode?.resourceUid
        if (postalCodeUid) {
            const postalCodeData = await getFrenchPostalCodeEntityCached(postalCodeUid)
            node.address.postalCode = postalCodeData
            if (node.address.postalCode) {
                if (node.address.postalCode.country) {
                    node.address.postalCode.country.name = 'France'
                }
                else {
                    node.address.postalCode.country = {
                        name: 'France'
                    }
                }
                node.address.postalCode.type = {
                    id: node.address.postalCode.resourceUid,
                    label: `${node.address.postalCode.city} - ${node.address.postalCode.postalCode}`
                }
            }
        }
    }
    return node;
}

export async function buildBalanceSheets(node: any) {
    if (node.status) {
        node.status = {
            id: node.status.resourceUid,
            label: node.status.resourceUid === 'NEW' ? 'Nouveau' : node.status.resourceUid === 'VALIDATED' ? 'Validé' : 'Refusé'
        }
    } else {
        node.status = {
            id: '',
            label: ''
        }
    }
    if (node.country) {
        node.country = {
            id: node.country.resourceUid,
            label: node.country.resourceUid === 'FRA' ? 'France' : 'Marroco'
        }
    } else {
        node.country = {
            id: '',
            label: ''
        }
    }
    if(node.period){
        node.period = {
            from: (node.period && node.period.from) ? node.period.from : '',
            until: (node.period && node.period.until) ? node.period.until : ''
        }
    }else{
        node.period = {
            from: '',
            until: ''
        }
    }

    const promises = node.detail.map((item: any) => buildBalanceSheetsItem(item))
    await Promise.all(promises)
    return node
}

export async function buildBalanceSheetsItem(node: any) {
    if (node.nature && node.nature.resourceUid) {
        const key = sanitizeKey(node.nature.resourceUid);
        node.nature = {
            id: node.nature.resourceUid,
            label: i18n.global.t(`party.natureBalanceSheet.${key}`),
        };
    }
}


export function filterParty(filter: FilterParty, offset?: any, limit?: any): Promise<FilterParty[]> {
    return new Promise<FilterParty[]>(async (resolve, reject) => {
        const url = settings.api_query_url;
        const qc = getQcParty(filter, offset, limit);
        const _headers = headers({
            qc: JSON.stringify(qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}




export function searchPerson(filter: SearchPerson): Promise<SearchPerson[]> {
    return new Promise<SearchPerson[]>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'party-person-search',
                "offset": 0,
                "limit": -1
            }
        }

        qc_header.qc.parameters = {
            ...filter
        }
        const operators = []
        if (filter.discriminator) {
            operators.push(getOperator("party_party", "_discriminator", "EQUAL", `'${filter.discriminator}'`, "discriminator"))
        }

        if (filter.reference) {
            operators.push(getOperator("party_party", "reference", "LIKE", globalConfig.formatters.getLikeValue(filter.reference), "reference"))
        }

        if (filter.firstName) {
            operators.push(getOperator("party_party", "first_name", "LIKE", globalConfig.formatters.getLikeValue(filter.firstName), "firstName"))
        }

        if (filter.familyName) {
            operators.push(getOperator("party_party", "family_name", "LIKE", globalConfig.formatters.getLikeValue(filter.familyName), "familyName"))
        }

        if (filter.birthDate) {
            operators.push(getOperator("party_party", "birth_date", "EQUAL", globalConfig.formatters.getLikeValue(formatDateLocale(filter.birthDate, 'DD/MM/YYYY', 'fr')), "birthDate"))
        }

        if (filter.phoneNumber) {
            operators.push(getOperator("party_contact_mechanism", "phone_number", "LIKE", null, "phoneNumber"))
        }

        if (filter.territory) {
            operators.push(getOperator("party_address", "territory", "LIKE", globalConfig.formatters.getLikeValue(filter.territory), "city"))
        }

        if (filter.postalCode) {
            filter.postalCode = filter.postalCode.config.postal_code
            operators.push(getOperator("party_address", "postal_code_id", "EQUAL", globalConfig.formatters.getLikeValue(filter.postalCode), "postalCode"))
        }
        if (filter.role) {
            operators.push(getOperator("party_party_role", "role_code", "LIKE", globalConfig.formatters.getLikeValue(filter.role), "role_code"))
        }
       // operators.push(getOperator("party_party_role", "status_code", "NOT_EQUAL", "'CLOSED'", "status_code"))
        //qc_header.qc.discriminator="party-person";
        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        and: {
                            operators
                        }
                    }
                ]
            }
        }

        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}

export function searchBankAgency(filter: SearchBankAgency): Promise<BankAgency[]> {
    return new Promise<BankAgency[]>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'simulation-party-bank-agency-search',
                offset: 0,
                limit: 100
            }
        }

        qc_header.qc.parameters = {
            ...filter
        }
        const operators = []

        if (filter.name) {
            operators.push(getOperator("party_bank_agency", "name", "LIKE", globalConfig.formatters.getLikeValue(filter.name), "name"))
        }

        if (filter.code) {
            operators.push(getOperator("party_bank_agency", "code", "LIKE", globalConfig.formatters.getLikeValue(filter.code), "code"))
        }

        if (filter.bankId) {
            operators.push(getOperator("party_bank_agency", "bank_id", "LIKE", globalConfig.formatters.getLikeValue(filter.bankId), "bank_id"))
        }

        if (filter.codeSwift) {
            operators.push(getOperator("party_bank_agency", "code_swift", "LIKE", globalConfig.formatters.getLikeValue(filter.codeSwift), "code_swift"))
        }

        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        or: {
                            operators
                        }
                    }
                ]
            }
        }

        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}

export function searchSupplierAddress(filter: any): Promise<any> {
    return new Promise<SearchOrganization[]>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'asset-supplier-address-search',
                "offset": 0,
                "limit": 100
            }
        }

        qc_header.qc.parameters = {
            ...filter
        }
        filter.preference_type_code = 'LIVRAISON'
        const operators = []
        operators.push(getOperator("party_party_preference", "preference_type_code", "EQUAL", `'${filter.preference_type_code}'`, "preference_type_code"))

        if (filter.id) {
            operators.push(getOperator("party_party", "id", "EQUAL", `'${filter.id}'`, "id"))
        }
        if (filter.offer_associated_party) {
            operators.push(getOperator("offer_associated_party", "id", "LIKE", globalConfig.formatters.getLikeValue(filter.offer_associated_party), "offer_associated_party"))
        }
        if (filter.street_num) {
            operators.push(getOperator("party_address", "street_num", "LIKE", globalConfig.formatters.getLikeValue(filter.street_num), "street_num"))
        }
        if (filter.street) {
            operators.push(getOperator("party_address", "street", "LIKE", globalConfig.formatters.getLikeValue(filter.street), "street"))
        }
        if (filter.line_1) {
            operators.push(getOperator("party_address", "line_1", "LIKE", globalConfig.formatters.getLikeValue(filter.line_1), "line_1"))
        }
        if (filter.postalCode) {
            operators.push(getOperator("party_postal_code", "id", "LIKE", globalConfig.formatters.getLikeValue(filter.postalCode), "postalCode"))
        }
        if (filter.city) {
            operators.push(getOperator("party_postal_code", "id", "LIKE", globalConfig.formatters.getLikeValue(filter.city), "city"))
        }
        if (filter.country_code) {
            operators.push(getOperator("party_postal_code", "country_code", "LIKE", globalConfig.formatters.getLikeValue(filter.country_code), "country_code"))
        }

        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        and: {
                            operators
                        }
                    }
                ]
            }
        }


        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}

export function searchOrganization(filter: SearchOrganization): Promise<SearchOrganization[]> {
    return new Promise<SearchOrganization[]>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'party-organization-search',
                "offset": 0,
                "limit": -1
            }
        }

        qc_header.qc.parameters = {
            ...filter
        }
        const operators = []
        if (filter.discriminator) {
            operators.push(getOperator("party_party", "_discriminator", "EQUAL", `'${filter.discriminator}'`, "discriminator"))
        }

        if (filter.reference) {
            operators.push(getOperator("party_party", "reference", "LIKE", globalConfig.formatters.getLikeValue(filter.reference), "reference"))
        }

        if (filter.name) {
            operators.push(getOperator("party_party", "name", "LIKE", globalConfig.formatters.getLikeValue(filter.name), "name"))
        }

        if (filter.territory) {
            operators.push(getOperator("party_postal_code", "city", "LIKE", globalConfig.formatters.getLikeValue(filter.territory), "city"))
        }

        if (filter.postalCode) {
            filter.postalCode = filter.postalCode.config.postal_code
            operators.push(getOperator("party_postal_code", "postal_code", "LIKE", globalConfig.formatters.getLikeValue(filter.postalCode), "postalCode"))
        }

        if (filter.siren) {
            operators.push(getOperator("party_party", "siren", "LIKE", globalConfig.formatters.getLikeValue(filter.siren), "siren"))
        }
        if (filter.id) {
            operators.push(getOperator("party_party", "id", "LIKE", globalConfig.formatters.getLikeValue(filter.id), "id"))
        }
        if (filter.role) {
            operators.push(getOperator("party_party_role", "role_code", "LIKE", globalConfig.formatters.getLikeValue(filter.role), "role_code"))
        }
        // operators.push(getOperator("party_party_role", "status_code", "NOT_EQUAL", "'CLOSED'", "status_code"))

        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        and: {
                            operators
                        }
                    }
                ]
            }
        }


        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}

export function searchOrganizationAsset(filter: SearchOrganization): Promise<SearchOrganization[]> {
    return new Promise<SearchOrganization[]>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'simulation-party-asset-organization-search',
                "offset": 0,
                "limit": 100
            }
        }

        qc_header.qc.parameters = {
            ...filter
        }
        const operators = []
        if (filter.discriminator) {
            operators.push(getOperator("party_party", "_discriminator", "EQUAL", `'${filter.discriminator}'`, "discriminator"))
        }

        if (filter.reference) {
            operators.push(getOperator("party_party", "reference", "LIKE", globalConfig.formatters.getLikeValue(filter.reference), "reference"))
        }

        if (filter.name) {
            operators.push(getOperator("party_party", "name", "LIKE", globalConfig.formatters.getLikeValue(filter.name), "name"))
        }

        if (filter.territory) {
            operators.push(getOperator("party_postal_code", "city", "LIKE", globalConfig.formatters.getLikeValue(filter.territory), "city"))
        }

        if (filter.postalCode) {
            operators.push(getOperator("party_postal_code", "postal_code", "LIKE", globalConfig.formatters.getLikeValue(filter.postalCode), "postalCode"))
        }

        if (filter.siren) {
            operators.push(getOperator("party_party", "siren", "LIKE", globalConfig.formatters.getLikeValue(filter.siren), "siren"))
        }
        if (filter.id) {
            operators.push(getOperator("party_party", "id", "LIKE", globalConfig.formatters.getLikeValue(filter.id), "id"))
        }
        if (filter.asset) {
            operators.push(getOperator("product_product_feature ", "id", "LIKE", globalConfig.formatters.getLikeValue(filter.asset), "id"))
        }

        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        and: {
                            operators
                        }
                    }
                ]
            }
        }


        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}

export function searchParties(filter: SearchPerson): Promise<SearchPerson[]> {
    return new Promise<SearchPerson[]>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'simulation-party-search',
                "offset": 0,
                "limit": 100
            }
        }

        qc_header.qc.parameters = {
            ...filter
        }

        const operators = []
        if (filter.firstName) {
            operators.push(getOperator("party_party", "name", "LIKE", null, "name"))
        }


        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        and: {
                            operators
                        }
                    }
                ]
            }
        }



        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}

export function searchSupplier(filter: SearchSupplier): Promise<SearchSupplier[]> {
    return new Promise<SearchSupplier[]>(async (resolve, reject) => {
        const url = settings.api_query_url;

        const qc_header: any = {
            qc: {
                queryId: 'simulation-party-supplier-search',
                "offset": 0,
                "limit": 100
            }
        }

        qc_header.qc.parameters = {
            ...filter
        }
        const operators = []
        if (filter.discriminator) {
            operators.push(getOperator("party_party", "_discriminator", "EQUAL", `'${filter.discriminator}'`, "discriminator"))
        }

        if (filter.reference) {
            operators.push(getOperator("party_party", "reference", "LIKE", globalConfig.formatters.getLikeValue(filter.reference), "reference"))
        }
        if (filter.name) {
            operators.push(getOperator("party_party", "name", "LIKE", globalConfig.formatters.getLikeValue(filter.name), "name"))
        }

        if (filter.territory) {
            operators.push(getOperator("party_postal_code", "city", "LIKE", globalConfig.formatters.getLikeValue(filter.territory), "city"))
        }

        if (filter.postalCode) {
            operators.push(getOperator("party_postal_code", "postal_code", "LIKE", globalConfig.formatters.getLikeValue(filter.postalCode), "postalCode"))
        }

        if (filter.siren) {
            operators.push(getOperator("party_party", "siren", "LIKE", globalConfig.formatters.getLikeValue(filter.siren), "siren"))
        }

        if (filter.productCode) {
            operators.push(getOperator("product_provider_intervention_zone ", "product_code", "LIKE", globalConfig.formatters.getLikeValue(filter.productCode), "product_code"))
        }

        if (operators.length > 0) {
            qc_header.qc.otherFilters = {
                expressions: [
                    {
                        and: {
                            operators
                        }
                    }
                ]
            }
        }


        const _headers = headers({
            qc: JSON.stringify(qc_header.qc)
        })
        axios.get(url, {
            headers: _headers
        }).then(res => {
            resolve(res.data)
        }).catch(err => reject(err))
    })
}
